// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/hooks/useNavigation.tsx
// v1.1.0 - Unterstützt menuName oder menuId

import { useState, useEffect } from 'react';

export interface NavigationItem {
  id: string;
  parent_id: string | null;
  label: string;
  href: string | null;
  icon: string | null;
  has_dropdown: boolean;
  dropdown_type: string | null;
  target: string;
  sort_order: number;
  children: NavigationItem[];
}

export interface NavigationMenu {
  name: string;
  display_name: string;
  items: NavigationItem[];
}

interface UseNavigationResult {
  menu: NavigationMenu | null;
  items: NavigationItem[];
  isLoading: boolean;
  error: string | null;
}

interface UseNavigationOptions {
  menuName?: string;
  menuId?: string | null;
}

export const useNavigation = (options: UseNavigationOptions | string = 'header'): UseNavigationResult => {
  const [menu, setMenu] = useState<NavigationMenu | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Normalize options
  const menuName = typeof options === 'string' ? options : options.menuName;
  const menuId = typeof options === 'string' ? undefined : options.menuId;

  useEffect(() => {
    const fetchNavigation = async () => {
      // Wenn menuId null ist (Kein Header), nichts laden
      if (menuId === null) {
        setMenu(null);
        setIsLoading(false);
        setError(null);
        return;
      }

      try {
        let url: string;
        
        if (menuId) {
          // Laden per ID
          url = `/system/api/v1/website/public/menu-by-id/${menuId}`;
        } else if (menuName) {
          // Laden per Name (Fallback)
          url = `/system/api/v1/website/public/menu/${menuName}`;
        } else {
          setMenu(null);
          setIsLoading(false);
          return;
        }

        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Failed to load navigation');
        }
        
        const data = await response.json();
        setMenu(data);
        setError(null);
      } catch (err) {
        console.warn('Could not load navigation from CMS:', err);
        setError('Navigation could not be loaded');
        setMenu(null);
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchNavigation();
  }, [menuName, menuId]);

  return {
    menu,
    items: menu?.items || [],
    isLoading,
    error
  };
};

export default useNavigation;
